import React, { useEffect, useState } from "react";
import { Button, Card, Input, SpinLoading, Toast } from "antd-mobile";
import { EnvironmentOutline, InformationCircleOutline } from "antd-mobile-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { DELIVERY_NATIVE_PATH_PREFIX, DeliveryNativePath, UserMenuPath } from "../../../../../enums/DeliveryNavigatePathEnum";
import { t } from "../../../../../i18n";
import { client } from "../../../../../graphql/client";
import { isLoggedIn, probeLoggedInBackendWithToast } from "../../../../../utils/auth";
import {
    DriverRouteEndPointAddressResponse,
    UPSERT_MY_DRIVER_ROUTE_START_END_POINT,
    UpsertMyDriverRouteStartEndPointInput,
    UpsertMyDriverRouteStartEndPointResponse,
    UpsertMyDriverRouteStartEndPointVariables,
} from "../../../../../graphql/mutations/driverRouteStartEndPoint";
import {
    GET_MY_DRIVER_ROUTE_START_END_POINT,
    GetMyDriverRouteStartEndPointResponse,
} from "../../../../../graphql/queries/driverRouteStartEndPoint";

type SectionKey = "start" | "end";

interface RouteEndpointsFormProps {}

interface EndpointFormData {
    country: string;
    state: string;
    city: string;
    district: string;
    detailAddress: string;
    latitude?: number;
    longitude?: number;
}

type FieldKey = "country" | "state" | "city" | "district" | "detailAddress";

interface FieldItem {
    key: FieldKey;
    labelKey: string;
    required?: boolean;
}

const ADDRESS_FIELDS: FieldItem[] = [
    { key: "country", labelKey: "pages.delivery.routeEndpoints.setting.fields.country", required: true },
    { key: "state", labelKey: "pages.delivery.routeEndpoints.setting.fields.state", required: true },
    { key: "city", labelKey: "pages.delivery.routeEndpoints.setting.fields.city", required: true },
    { key: "district", labelKey: "pages.delivery.routeEndpoints.setting.fields.district" },
    { key: "detailAddress", labelKey: "pages.delivery.routeEndpoints.setting.fields.detailAddress", required: true },
];

const EMPTY_ENDPOINT: EndpointFormData = {
    country: "",
    state: "",
    city: "",
    district: "",
    detailAddress: "",
};

const MAP_PICKER_RESULT_KEY = "delivery_route_endpoints_map_picker_result";
const SNAPSHOT_KEY = "delivery_route_endpoints_snapshot";
const MAP_PICKER_RESULT_MAX_AGE_MS = 5 * 60 * 1000;

const normalizeEndpoint = (
    value?: Partial<EndpointFormData> | DriverRouteEndPointAddressResponse | null
): EndpointFormData => ({
    country: value?.country ?? "",
    state: value?.state ?? "",
    city: value?.city ?? "",
    district: value?.district ?? "",
    detailAddress: value?.detailAddress ?? "",
    latitude: value?.latitude ?? undefined,
    longitude: value?.longitude ?? undefined,
});

const RouteEndpointsForm: React.FC<RouteEndpointsFormProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [start, setStart] = useState<EndpointFormData>(EMPTY_ENDPOINT);
    const [end, setEnd] = useState<EndpointFormData>(EMPTY_ENDPOINT);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);

    const syncEndpointStates = (
        nextStart?: Partial<EndpointFormData> | DriverRouteEndPointAddressResponse | null,
        nextEnd?: Partial<EndpointFormData> | DriverRouteEndPointAddressResponse | null
    ) => {
        setStart(normalizeEndpoint(nextStart));
        setEnd(normalizeEndpoint(nextEnd));
    };

    const reloadFromServer = async () => {
        const { data, errors } = await client.query<GetMyDriverRouteStartEndPointResponse>({
            query: GET_MY_DRIVER_ROUTE_START_END_POINT,
            fetchPolicy: "network-only",
            errorPolicy: "all",
        });

        const savedEndpoints = data?.getMyDriverRouteStartEndPoint;
        if (!savedEndpoints || errors?.length) {
            syncEndpointStates(EMPTY_ENDPOINT, EMPTY_ENDPOINT);
            return;
        }

        syncEndpointStates(savedEndpoints.start, savedEndpoints.end);
    };

    const syncFromPickerResult = (): boolean => {
        const raw = sessionStorage.getItem(MAP_PICKER_RESULT_KEY);
        if (!raw) {
            return false;
        }

        try {
            const result = JSON.parse(raw) as {
                section?: SectionKey;
                confirmedAt?: number;
                country?: string;
                state?: string;
                city?: string;
                district?: string;
                detailAddress?: string;
                latitude?: number;
                longitude?: number;
            };
            const confirmedAt = typeof result.confirmedAt === "number" ? result.confirmedAt : null;
            const isFreshResult = confirmedAt != null && Date.now() - confirmedAt >= 0 && Date.now() - confirmedAt <= MAP_PICKER_RESULT_MAX_AGE_MS;
            if (!isFreshResult) {
                return false;
            }

            if (result.section === "start") {
                setStart((prev) => ({
                    ...prev,
                    country: result.country ?? prev.country,
                    state: result.state ?? prev.state,
                    city: result.city ?? prev.city,
                    district: result.district ?? prev.district,
                    detailAddress: result.detailAddress ?? prev.detailAddress,
                    latitude: result.latitude ?? prev.latitude,
                    longitude: result.longitude ?? prev.longitude,
                }));
                return true;
            }

            if (result.section === "end") {
                setEnd((prev) => ({
                    ...prev,
                    country: result.country ?? prev.country,
                    state: result.state ?? prev.state,
                    city: result.city ?? prev.city,
                    district: result.district ?? prev.district,
                    detailAddress: result.detailAddress ?? prev.detailAddress,
                    latitude: result.latitude ?? prev.latitude,
                    longitude: result.longitude ?? prev.longitude,
                }));
                return true;
            }
            return false;
        } finally {
            sessionStorage.removeItem(MAP_PICKER_RESULT_KEY);
        }
    };

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                syncFromPickerResult();
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadSavedEndpoints = async () => {
            try {
                const snapshotRaw = sessionStorage.getItem(SNAPSHOT_KEY);
                if (snapshotRaw) {
                    sessionStorage.removeItem(SNAPSHOT_KEY);
                    const snapshot = JSON.parse(snapshotRaw) as { start?: EndpointFormData; end?: EndpointFormData };
                    syncEndpointStates(snapshot.start, snapshot.end);
                } else {
                    await reloadFromServer();
                }
            } catch (error) {
                if (isMounted) {
                    syncEndpointStates(EMPTY_ENDPOINT, EMPTY_ENDPOINT);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                    syncFromPickerResult();
                }
            }
        };

        loadSavedEndpoints();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getValueBySection = (section: SectionKey): EndpointFormData => {
        return section === "start" ? start : end;
    };

    const setValueBySection = (section: SectionKey, value: EndpointFormData) => {
        if (section === "start") {
            setStart(value);
            return;
        }

        setEnd(value);
    };

    const isEndpointEmpty = (value: EndpointFormData) => {
        return (
            !value.country.trim() &&
            !value.state.trim() &&
            !value.city.trim() &&
            !value.district.trim() &&
            !value.detailAddress.trim() &&
            value.latitude == null &&
            value.longitude == null
        );
    };

    const getMissingRequiredFieldLabel = (value: EndpointFormData): string | null => {
        const missingField = ADDRESS_FIELDS.find((field) => {
            if (!field.required) {
                return false;
            }

            return !value[field.key].trim();
        });

        return missingField ? t(missingField.labelKey) : null;
    };

    const handleCancel = () => {
        setResetting(true);
        reloadFromServer()
            .catch(() => {
                Toast.show({
                    content: t("common.backendProbe.error"),
                    position: "center",
                });
            })
            .finally(() => {
                setResetting(false);
            });
    };

    const handleConfirm = async () => {
        const startIsEmpty = isEndpointEmpty(start);
        const endIsEmpty = isEndpointEmpty(end);

        if (startIsEmpty && endIsEmpty) {
            return;
        }

        if (!startIsEmpty) {
            const missingFieldLabel = getMissingRequiredFieldLabel(start);
            if (missingFieldLabel) {
                Toast.show({
                    content: t("pages.delivery.routeEndpoints.setting.validation.requiredField", {
                        field: missingFieldLabel,
                    }),
                    position: "center",
                });
                return;
            }
        }
        if (!endIsEmpty) {
            const missingFieldLabel = getMissingRequiredFieldLabel(end);
            if (missingFieldLabel) {
                Toast.show({
                    content: t("pages.delivery.routeEndpoints.setting.validation.requiredField", {
                        field: missingFieldLabel,
                    }),
                    position: "center",
                });
                return;
            }
        }

        setSaving(true);
        try {
            await probeLoggedInBackendWithToast({
                isLoggedIn,
                notLoggedInText: t("common.backendProbe.notLoggedIn"),
                loadingText: t("common.backendProbe.loading"),
                errorText: t("common.backendProbe.error"),
                request: async () => {
                    const input: UpsertMyDriverRouteStartEndPointInput = {};
                    if (!startIsEmpty) {
                        input.start = { ...start };
                    }
                    if (!endIsEmpty) {
                        input.end = { ...end };
                    }

                    const { data, errors } = await client.mutate<
                        UpsertMyDriverRouteStartEndPointResponse,
                        UpsertMyDriverRouteStartEndPointVariables
                    >({
                        mutation: UPSERT_MY_DRIVER_ROUTE_START_END_POINT,
                        variables: { input },
                        errorPolicy: "all",
                    });

                    if (errors?.length) {
                        throw errors[0];
                    }

                    const savedEndpoints = data?.upsertMyDriverRouteStartEndPoint;
                    if (savedEndpoints) {
                        syncEndpointStates(savedEndpoints.start, savedEndpoints.end);
                    }

                    return savedEndpoints;
                },
                getSuccessText: () => t("common.backendProbe.success"),
            });
        } finally {
            setSaving(false);
        }
    };

    const onMapPin = (section: SectionKey) => {
        const current = getValueBySection(section);
        sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ start, end }));
        const params = new URLSearchParams();
        params.set("section", section);
        params.set("title", t("pages.delivery.routeEndpoints.mapPicker.title"));
        if (current.latitude != null) {
            params.set("lat", String(current.latitude));
        }
        if (current.longitude != null) {
            params.set("lng", String(current.longitude));
        }
        if (current.detailAddress) {
            params.set("address", current.detailAddress);
        }
        // native 入口(NativeApp)只注册 /delivery-native/* 路由，跳 main 入口的地图标点页会白屏
        const mapPickerPath = location.pathname.startsWith(DELIVERY_NATIVE_PATH_PREFIX)
            ? DeliveryNativePath.SETTINGS_ROUTE_ENDPOINTS_MAP_PICKER
            : UserMenuPath.USER_MENU_SETTINGS_ROUTE_ENDPOINTS_MAP_PICKER_NATIVE;
        navigate(`${mapPickerPath}?${params.toString()}`);
    };

    const renderSection = (section: SectionKey) => {
        const value = getValueBySection(section);
        const sectionTitle =
            section === "start"
                ? t("pages.delivery.routeEndpoints.setting.sections.start")
                : t("pages.delivery.routeEndpoints.setting.sections.end");
        const mapButtonText = t("pages.delivery.routeEndpoints.setting.mapButtonText");

        return (
            <Card key={section} className="mb-3" bodyStyle={{ padding: "14px 12px" }}>
                <div className="flex items-center justify-between mb-3">
                    <div className="text-base font-semibold text-zinc-800">{sectionTitle}</div>
                    <Button
                        size="small"
                        color="primary"
                        fill="outline"
                        onClick={() => onMapPin(section)}
                        disabled={saving || resetting}
                    >
                        <span className="inline-flex items-center gap-1 font-medium">
                            <EnvironmentOutline />
                            <span>{mapButtonText}</span>
                        </span>
                    </Button>
                </div>
                <div className="space-y-2">
                    {ADDRESS_FIELDS.map((field) => (
                        <div key={`${section}-${field.key}`} className="flex items-start gap-2">
                            <div className="w-20 text-sm font-medium text-zinc-800 break-words whitespace-normal leading-5">
                                {field.required ? <span className="text-red-500 mr-1">*</span> : null}
                                {t(field.labelKey)}
                            </div>
                            <Input
                                className="flex-1"
                                placeholder={t("pages.delivery.routeEndpoints.setting.inputPlaceholder")}
                                clearable
                                value={value[field.key]}
                                onChange={(val) =>
                                    setValueBySection(section, {
                                        ...value,
                                        [field.key]: val,
                                    })
                                }
                            />
                        </div>
                    ))}
                </div>
            </Card>
        );
    };

    if (loading) {
        return (
            <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-sm text-zinc-500">
                <SpinLoading color="primary" className="text-[32px]" />
                <div>{t("pages.delivery.routeEndpoints.setting.loading")}</div>
            </div>
        );
    }

    return (
        <div className="px-3 pb-4 overflow-auto">
            <div
                className="my-3 rounded-md px-3 py-2"
                style={{
                    backgroundColor: "#FFF7E6",
                }}
            >
                <div className="flex items-start gap-2">
                    <InformationCircleOutline
                        className="flex-shrink-0"
                        style={{ color: "#FA8C16", marginTop: 2, fontSize: 18 }}
                    />
                    <div
                        className="text-sm leading-5"
                        style={{
                            color: "#FA8C16",
                            fontWeight: 600,
                        }}
                    >
                        {t("pages.delivery.routeEndpoints.setting.hint")}
                    </div>
                </div>
            </div>
            {renderSection("start")}
            {renderSection("end")}
            <div className="flex gap-3 mt-4">
                <Button
                    color="default"
                    onClick={handleCancel}
                    loading={resetting}
                    disabled={saving}
                    block
                    style={{
                        "--background-color": "gray",
                        "--text-color": "white",
                        fontWeight: 600,
                    }}
                >
                    {t("pages.delivery.routeEndpoints.mapPicker.cancel")}
                </Button>
                <Button
                    color="primary"
                    onClick={handleConfirm}
                    loading={saving}
                    disabled={resetting || (isEndpointEmpty(start) && isEndpointEmpty(end))}
                    block
                    style={{ fontWeight: 600 }}
                >
                    {t("pages.delivery.routeEndpoints.mapPicker.confirm")}
                </Button>
            </div>
        </div>
    );
};

export default RouteEndpointsForm;
